import React, { useState } from 'react';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, MenuItem } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { CompanyService, GenerationJobsService, RetrieveDocumentGenerationJob } from 'api';
import { useResponse } from 'services/hooks';
import DocumentGenerationJobTableItemFinMapMenuProps from './DocumentGenerationJobTableItemFinMapMenu.props';
import { useStore } from '../../../../../../common/store';
import { UseStoreValues } from '../../../../../../common/types/store';
import { useAsyncProcessor } from '../../../../../../context/AsyncProcessorContext/AsyncProcessorContext';
import { Logger } from '../../../../../../utilities';
import { JSONGenerationJobParsingMode } from '../../../../models/enums/generation-job-parsing-mode.enum';

const DocumentGenerationJobTableItemFinMapMenu = ({
  generationJob,
  onClose,
}: DocumentGenerationJobTableItemFinMapMenuProps) => {
  const [loading, setLoading] = useState(false);
  const [reprocessDialogOpen, setReprocessDialogOpen] = useState(false);
  const { successNotification, errorNotification } = useResponse();
  const { addJob, removeJob } = useAsyncProcessor();
  const [storeValue] = useStore() as unknown as UseStoreValues;
  const { companyInfo } = storeValue;

  const handleCreateMapping = async () => {
    setLoading(true);

    try {
      Logger.info(`Creating attribute mapping for job ${generationJob.job_id}`);

      await GenerationJobsService.apiGenerationJobsAttributeMappingsCreate(generationJob.job_id);

      successNotification('Financial mapping created and associated with financial period');
      
      // Close menu after successful creation
      if (onClose) {
        onClose();
      }
    } catch (err: any) {
      Logger.error('Error creating attribute mapping:', err);
      const errorMessage = err?.body?.detail || err?.message || 'Failed to create mapping';
      errorNotification(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleReprocessClick = () => {
    setReprocessDialogOpen(true);
  };

  const handleReprocessConfirm = async () => {
    setReprocessDialogOpen(false);
    setLoading(true);

    try {
      Logger.info(`Reprocessing document for job ${generationJob.job_id}`);

      if (!companyInfo?.id) {
        errorNotification('Company information not available');
        return;
      }

      // Get the document reference and measurement date from metadata
      const { document_reference_id, measurement_date_id, file_id } = generationJob.metadata;

      // Create a new generation job for the same document
      const newJob = await CompanyService.apiCompanyDocumentGenerationJobsNewCreate(companyInfo.id?.toString() || '', {
        processing_type: RetrieveDocumentGenerationJob.processing_type.FINANCIAL_MAPPING,
        reference_files: [file_id],
        desired_termination_state: 'COMPLETED',
        parser_overrides: [
          {
            file_type: 'json',
            json_parsing_mode: JSONGenerationJobParsingMode.XLSX_JSON_MINIFIER,
          },
        ],
        metadata: {
          document_reference_id,
          measurement_date_id,
          file_id,
        },
      });

      // Add the new job to polling
      Logger.info('DocumentGenerationJobTableItemFinMapMenu:', `Adding reprocess job ${newJob.job_id} to polling`);
      
      addJob({
        id: newJob.job_id,
        details: {
          title: 'Reprocessing Document',
          description: `Reprocessing ${generationJob.job_id}`,
        },
        pollingConfig: {
          pollingFunction: async () => {
            const result = await CompanyService.apiCompanyDocumentGenerationJobsRead(companyInfo.id?.toString() || '', newJob.job_id);
            return result;
          },
          pollingInterval: 3000,
          pollingArgs: [],
        },
        onResult: async (result: RetrieveDocumentGenerationJob) => {
          if (result.status === RetrieveDocumentGenerationJob.status.COMPLETED) {
            try {
              // Auto-create/merge the mapping
              await GenerationJobsService.apiGenerationJobsAttributeMappingsCreate(result.job_id);
              enqueueSnackbar('Reprocessing completed - mapping updated', { variant: 'success' });
            } catch (error) {
              Logger.error('DocumentGenerationJobTableItemFinMapMenu - Error creating mapping for reprocessed job', error);
              enqueueSnackbar('Job completed but mapping update failed', { variant: 'warning' });
            }
            removeJob(result.job_id);
          } else if (result.status === RetrieveDocumentGenerationJob.status.FAILED) {
            enqueueSnackbar('Reprocessing job failed', { variant: 'error' });
            removeJob(result.job_id);
          }
        },
        onError: (error: Error) => {
          Logger.error(
            `DocumentGenerationJobTableItemFinMapMenu - Reprocess job ${newJob.job_id} polling error: ${error.message}`,
            error
          );
        },
      });

      successNotification('Reprocessing started. New suggestions will appear when complete.');
      
      // Close menu after successful creation
      if (onClose) {
        onClose();
      }
    } catch (err: any) {
      Logger.error('Error reprocessing document:', err);
      const errorMessage = err?.body?.detail || err?.message || 'Failed to reprocess document';
      errorNotification(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleReprocessCancel = () => {
    setReprocessDialogOpen(false);
  };

  // Check if the job has metadata (required for auto-completion)
  const hasMetadata =
    generationJob.metadata &&
    generationJob.metadata.document_reference_id &&
    generationJob.metadata.measurement_date_id;

  if (!hasMetadata) {
    return (
      <MenuItem disabled>
        <Box display="flex" alignItems="center" gap={1}>
          No metadata available
        </Box>
      </MenuItem>
    );
  }

  return (
    <>
      <MenuItem onClick={handleCreateMapping} disabled={loading || generationJob.status !== RetrieveDocumentGenerationJob.status.COMPLETED}>
        <Box display="flex" alignItems="center" gap={1}>
          {loading && <CircularProgress size={16} />}
          {loading ? 'Creating Mapping...' : 'Create Financial Mapping'}
        </Box>
      </MenuItem>
      
      <MenuItem onClick={handleReprocessClick} disabled={loading || generationJob.status !== RetrieveDocumentGenerationJob.status.COMPLETED}>
        <Box display="flex" alignItems="center" gap={1}>
          {loading && <CircularProgress size={16} />}
          Reprocess Document
        </Box>
      </MenuItem>

      <Dialog open={reprocessDialogOpen} onClose={handleReprocessCancel}>
        <DialogTitle>Reprocess Document</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will reprocess the document with the AI mapping system. Accepted and human-created mappings will be preserved. 
            Only unaccepted AI suggestions will be replaced with new ones.
            <br /><br />
            Do you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReprocessCancel} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleReprocessConfirm} color="primary" variant="contained">
            Reprocess
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DocumentGenerationJobTableItemFinMapMenu;
